// Central data file for all shipped projects.
// Each entry powers /projects (list) and /projects/:slug (case study).
// Case-study copy is drafted from live-site facts — safe to edit any field.

const projects = [
  {
    slug: "affordable-stump-grinding",
    name: "Affordable Stump Grinding",
    url: null,
    tagline: "Invoices, tracked. Cash flow, visible.",
    category: "Web App · Small Business Ops",
    year: "2026",
    image: "/portfolio/affordable-stump-grinding.jpg",
    summary:
      "A lightweight invoicing and cash-flow dashboard for a local stump grinding business — track unpaid balances, flag overdue jobs, and see paid-this-month at a glance.",
    problem:
      "Owner-operator trade businesses live in spreadsheets and shoeboxes. Invoices get lost, overdue jobs slip through the cracks, and the owner has no clear picture of what's actually owed at the end of any given week. Off-the-shelf tools like QuickBooks are overkill and slow to open on a phone between jobs.",
    solution:
      "A purpose-built invoicing app that fits how a two-person crew actually works. Every invoice has a status (Unpaid, Overdue, Paid), the dashboard shows Unpaid Balance, Overdue count, and Paid This Month at the top, and creating a new invoice takes under 30 seconds. Runs in any browser — no install, no per-seat fees.",
    highlights: [
      "Live dashboard: Unpaid Balance, Overdue count, Paid This Month",
      "Status filters (All / Unpaid / Overdue / Paid) and instant search",
      "One-tap New Invoice with customer + service + date + amount",
      "Mobile-first — usable one-handed between jobs",
    ],
    stack: ["React", "FastAPI", "MongoDB", "Emergent"],
    outcome:
      "The owner sees exactly what's owed and what's overdue every time he opens the app — no spreadsheets, no shoeboxes, no guessing what payroll week actually looks like.",
    metaDescription:
      "Affordable Stump Grinding — a lightweight invoicing and cash-flow dashboard for a local trade business. Built by Gadget Guy Media.",
  },
  {
    slug: "ezscribe",
    name: "ezScribe",
    url: "https://ezscribe.net",
    tagline: "Your notes decide what you get paid.",
    category: "AI SaaS · Healthcare",
    year: "2025",
    image: "/portfolio/ezscribe.jpg",
    summary:
      "HIPAA-compliant AI medical scribe for independent practices: ambient capture of the visit becomes a structured SOAP note with ICD-10 codes, ready for any EHR. $49 per provider per month.",
    problem:
      "Physicians and dentists lose hours every night finishing charts, and vague notes routinely get downcoded — meaning the visit gets paid at a lower rate than the work justified. The paperwork tax is real and it never stops.",
    solution:
      "Tap record and talk to the patient. OpenAI Whisper transcribes the visit, and Gemini Pro structures it into a SOAP note with specific ICD-10 codes and a patient-education handout. Copy it into any EHR in one click, or push it straight in with the free ezScribe Push Chrome extension.",
    highlights: [
      "Ambient one-tap capture → structured SOAP note with ICD-10 codes",
      "Transcribes 25 languages; one-click summary translation to Spanish or French",
      "Patient-education handouts drawn from ADA, AHA and AGA guidelines",
      "AES-256 at rest, TLS in transit; transcriptions auto-purged after 30 days",
      "Voice commands and ezMacros (.NORM, .ROS, .PLAN) for hands-free charting",
      "Flat pricing from $49/month, unlimited visits, 14-day free trial",
    ],
    stack: ["React", "FastAPI", "OpenAI Whisper", "Gemini Pro", "MongoDB", "Stripe", "Emergent"],
    outcome:
      "Clinicians report saving 1–2+ hours a day, and the note finally supports the level of service they actually delivered.",
    pricing: {
      note: "Start with a 14-day free trial with every feature unlocked. Card required, no charge for 14 days, cancel anytime. Annual billing saves 10%; students get 25% off.",
      trialUrl: "https://app.ezscribe.net/register?utm_source=gadgetguymedia&utm_medium=portfolio",
      plans: [
        { name: "Solo", price: "$49", users: "Individual providers", bestFor: "Everything you need to stop charting late", features: ["Unlimited transcriptions", "AI clinical summaries with ICD-10", "Voice commands & smart shortcuts", "Multilingual (English, Spanish & French)", "HIPAA-grade encryption"] },
        { name: "Specialist", price: "$64", users: "Referring providers", bestFor: "Hand off to specialists in one click", popular: true, features: ["Everything in Solo", "Specialist Share Hub", "EMR-ready PDF exports with letterhead", "AI-polished email handoffs", "Power Phrases clinical macros"] },
        { name: "Clinic", price: "$74", users: "Per provider", bestFor: "Branded handoffs for practices & groups", features: ["Everything in Specialist", "Custom practice branding & logo", "Custom letterhead colors", "Dragon Medical One macro import", "Custom ezMacros library"] },
      ],
    },
    metaDescription:
      "ezScribe: HIPAA-compliant AI medical scribe that turns a patient visit into a SOAP note with ICD-10 codes. Plans from $49/month with a 14-day free trial. Built by Gadget Guy Media.",
  },
  {
    slug: "ezscribe-app",
    name: "ezScribe App",
    url: "https://app.ezscribe.net",
    tagline: "The scribe, in your pocket.",
    category: "Web App · Healthcare",
    year: "2025",
    image: "/portfolio/ezscribe-app.jpg",
    summary:
      "The ezScribe workspace at app.ezscribe.net: dictate the visit, review the SOAP note and push it to your EHR from phone, tablet or desktop. Installs to the home screen like an app.",
    problem:
      "Clinicians move between exam rooms, offices, and home. A desktop-only tool leaves half the day uncovered, and native apps add app-store friction and update lag.",
    solution:
      "A secure web app with email or Google sign-in that runs on any device with the same account. Whisper transcribes in real time, Gemini structures the note, and notes go to the EHR via the Chrome extension, copy, PDF or share. It installs as a PWA on Android and iPhone, with offline recording drafts.",
    highlights: [
      "Same account across phone, tablet and desktop",
      "Installable PWA, no app store needed",
      "Offline recording drafts and viewing of past notes",
      "Auto sign-out after inactivity; HIPAA-compliant, BAA on request",
    ],
    stack: ["React (PWA)", "FastAPI", "OpenAI Whisper", "Gemini Pro", "MongoDB", "Emergent"],
    outcome:
      "Note completion happens between patients instead of after hours, because the tool is wherever the clinician is.",
    metaDescription:
      "The ezScribe web app — record, review, and export AI-generated medical notes from any device. Built by Gadget Guy Media.",
  },
  {
    slug: "ezscribe-chrome-add-in",
    name: "ezScribe Chrome Add-In",
    url: "https://ezscribe.net/push",
    tagline: "One click. Note in the chart.",
    category: "Browser Extension · Healthcare",
    year: "2025",
    image: "/portfolio/ezscribe-chrome.jpg",
    summary:
      "ezScribe Push: a free, Google-approved Chrome extension that pushes finished ezScribe notes straight into the note fields of browser-based EHRs, with no copy-paste.",
    problem:
      "Even with an AI note ready, clinicians still lose seconds per patient copying, pasting, and reformatting into the EHR — plus the risk of a mispaste.",
    solution:
      "Sign in with your ezScribe account, open the note editor in your EHR, pick the note and click Push. The extension reads field labels and headings to map each SOAP section, shows a confidence-rated preview you can override, and writes nothing until you confirm.",
    highlights: [
      "Supports Epic, athenahealth, eClinicalWorks, Tebra and Elation Health",
      "Mapping preview with High / Medium / Low confidence and manual override",
      "Session-only tokens, wiped when the browser closes; no analytics",
      "Free on the Chrome Web Store; works in Chrome, Edge and Brave",
    ],
    stack: ["Chrome Extension (MV3)", "JavaScript", "ezScribe API"],
    outcome:
      "Cuts the last friction point out of the charting workflow — record, review, click, done.",
    metaDescription:
      "The ezScribe Chrome extension pushes AI-generated notes straight into any web-based EHR in one click. Built by Gadget Guy Media.",
  },
  {
    slug: "ezbuildabot",
    name: "ezBuildabot",
    url: "https://ezbuildabot.com",
    tagline: "Custom AI agents. Built ez. Deployed faster.",
    category: "AI Agency · Landing",
    year: "2025",
    image: "/portfolio/ez-build-test.jpg",
    summary:
      "Custom AI agents, full-stack web apps and AI landing pages for solopreneurs and small businesses, with a working prototype in days, not months.",
    problem:
      "Small businesses know AI can help them, but the agency quotes land priced like enterprise projects, and the DIY tools leave them staring at a blank canvas.",
    solution:
      "A 30-minute discovery call maps the workflow, then a working prototype ships in 3–7 days, connected to your stack and trained on your data. Fixed, published pricing: a $297 Bot Blueprint Call (credited to the build), Starter Bots from $2,500 and Custom App Builds from $7,500, with hosting and monthly retainers to keep it running.",
    highlights: [
      "Average 4 days to first agent",
      "Support, sales and ops agents on web chat, email, Slack, WhatsApp or voice",
      "GPT / Claude / Gemini routing for cost-optimized intelligence",
      "Published, fixed pricing, with no \"contact us\" runaround",
      "Client owns the build",
    ],
    stack: ["React", "FastAPI", "MongoDB", "OpenAI", "Anthropic", "Google Gemini", "Stripe"],
    outcome:
      "The productized delivery model that powers Gadget Guy Media itself — proven by every other project on this list.",
    metaDescription:
      "ezBuildabot — done-for-you AI agents and apps, shipped in weeks not quarters. Built by Gadget Guy Media.",
  },
  {
    slug: "ezitemizer",
    name: "ezItemizer",
    url: "https://ezitemizer.com",
    tagline: "Inventory that survives the shop floor.",
    category: "SaaS · Inventory Control",
    year: "2025",
    image: "/portfolio/ezitemizer.jpg",
    summary:
      "Parts inventory for trailer manufacturers, warehouses and job sites: live stock counts, phone barcode scanning, low-stock push alerts and a full audit trail for every movement.",
    problem:
      "Small manufacturers track thousands of parts on spreadsheets and memory. Counts drift, nobody knows who pulled what, and a missing fender or axle stops a trailer build mid-line.",
    solution:
      "ezItemizer logs every IN, OUT and adjustment against a SKU and the operator who made it. Staff scan labels with their phone camera, and the threshold engine sends a push alert the moment a part hits its reorder line. It installs like an app and keeps working on flaky shop-floor wifi.",
    highlights: [
      "Real-time stock counts with a per-user audit trail",
      "Camera barcode + QR scanning, no hardware scanner needed",
      "Low-stock push and email alerts at each part's reorder point",
      "Three access roles: Admin, Warehouse Staff and Viewer (Viewers never see costs)",
      "CSV and branded PDF inventory and financial reports (margin, COGS, valuation)",
      "Installable PWA that works offline; plans from $29/month with a 14-day free trial",
    ],
    stack: ["React (PWA)", "FastAPI", "MongoDB", "JWT auth", "Web Push", "Stripe"],
    outcome:
      "Parts managers get real counts, alerts before a part runs out and a paper trail that holds up in an audit, on the phones and tablets already on the shop floor.",
    pricing: {
      note: "Every plan includes a 14-day free trial. Card required, no charge until day 15, cancel anytime.",
      trialUrl: "https://ezitemizer.com/#pricing",
      plans: [
        { name: "Basic", price: "$29", users: "Up to 2 users", bestFor: "Solo operators and one-person shops", features: ["Unlimited parts & SKUs", "Real-time stock + full audit trail", "Low-stock push & email alerts", "CSV & PDF inventory reports"] },
        { name: "Team", price: "$59", users: "Up to 5 users", bestFor: "Growing shops with warehouse staff", popular: true, features: ["Everything in Basic", "Camera barcode + QR scanning", "Warehouse Staff role", "End-of-day digest emails"] },
        { name: "Pro", price: "$89", users: "Up to 10 users", bestFor: "Multi-team operations and accounting", features: ["Everything in Team", "Financial reports (margin, COGS, valuation)", "PDF/CSV financial exports", "Priority email support"] },
      ],
    },
    metaDescription:
      "ezItemizer: parts inventory for trailer manufacturers and shops. Live stock counts, phone barcode scanning, low-stock push alerts and full audit trail. Built by Gadget Guy Media.",
  },
  {
    slug: "ezguard",
    name: "ezGuard",
    url: "https://getezguard.com",
    tagline:
      "The keys to your life, kept safe for the people who matter.",
    category:
      "Web App · Family Security",
    year: "2025",
    image: "/portfolio/ezguard.jpg",
    summary:
      "A calm, secure password vault for couples and families: one master password, item-by-item sharing with your spouse, and emergency access for trusted contacts.",
    problem:
      "Most families keep passwords in a notebook, a shared note or one person's head. When that person is unavailable, or something happens to them, the family is locked out of the bank, the bills and the Wi-Fi.",
    solution:
      "EZGuard keeps every login, secure note and card behind a single master password. You choose exactly which items to share with your spouse, with read or edit access, and designate trusted contacts who can request emergency access that auto-grants after a waiting period you set.",
    highlights: [
      "AES-256-GCM encryption at rest; bcrypt-hashed master password",
      "Share specific items with read or edit access",
      "Emergency access with a configurable waiting period",
      "Strong password generator with a live strength meter",
      "Invite-only accounts, sign-in lockout and httpOnly cookies",
      "Installable PWA on any device",
    ],
    stack: ["React (PWA)", "FastAPI", "MongoDB", "AES-256-GCM", "bcrypt"],
    outcome:
      "A family's digital keys stay private day to day, and still reachable by the right people when it matters most.",
    metaDescription:
      "EZGuard: a secure family password vault with AES-256 encryption, spouse sharing and emergency access for trusted contacts. Built by Gadget Guy Media.",
  },
  {
    slug: "car-value-recovery",
    name: "Car Value Recovery",
    url: "https://wreck-loss-appraiser.emergent.host",
    tagline: "The diminished-value claim your insurer hopes you won't file.",
    category: "AI App · Insurance",
    year: "2025",
    image: "/portfolio/car-value-recovery.jpg",
    summary:
      "A diminished-value compensation engine: upload the insurance estimate and get a printable, appraiser-grade retail-market loss report built to get insurers to pay.",
    problem:
      "Even a perfectly repaired car is worth less on Carfax the day after a wreck. Most owners never file a diminished-value claim because they don't know it exists or how to prove it.",
    solution:
      "Upload the insurance estimate PDF and AI fills in the vehicle, mileage, location and repair costs. Add condition, damage severity and high-stigma history like frame damage or deployed airbags, pick your state, and the engine produces a printable diminished-value dossier.",
    highlights: [
      "AI scan of the insurance estimate PDF auto-fills the form",
      "State-by-state diminished-value rules and deadlines",
      "Flags high-stigma history: structural/frame damage, airbag deployment",
      "Gas, electric, hybrid and diesel vehicles",
      "Printable appraiser-grade report; one-tap demo scenarios",
    ],
    stack: ["React", "FastAPI", "Claude", "MongoDB"],
    outcome:
      "Puts the tool for a check-on-the-table conversation in the hands of the person who actually paid for the wreck.",
    metaDescription:
      "Car Value Recovery — estimate diminished value on a repaired vehicle and file the claim insurers hope you won't. Built by Gadget Guy Media.",
  },
  {
    slug: "claude-code-cost-tracker",
    name: "Claude Code Cost Tracker",
    url: "https://claude-telemetry.emergent.host",
    tagline: "Know exactly what your AI-coded build cost.",
    category: "AI Tool · Developer",
    year: "2025",
    image: "/portfolio/claude-cost-tracker.jpg",
    summary:
      "An OpenTelemetry command center for Claude Code: ingest usage over HTTPS and see tokens, cost, sessions and raw events without digging through a billing console.",
    problem:
      "Anyone building with Claude Code sees the bill at the end of the month with no idea which project, feature, or day actually burned the tokens.",
    solution:
      "Point Claude Code's built-in OpenTelemetry export at the tracker's OTLP endpoints (/api/v1/metrics and /api/v1/logs). It normalizes the data and shows token, cost, session and raw-event views in a sign-in dashboard.",
    highlights: [
      "OTLP over HTTPS: metrics and logs endpoints",
      "Token and cost visibility per session",
      "Raw event explorer for debugging",
      "Password-protected dashboard",
    ],
    stack: ["React", "FastAPI", "MongoDB", "OpenTelemetry (OTLP)"],
    outcome:
      "Turns the AI-coding bill into a line-itemized statement you can actually price a project against.",
    metaDescription:
      "Claude Code Cost Tracker — real per-project, per-day telemetry for Claude Code sessions. Built by Gadget Guy Media.",
  },
  {
    slug: "family-health-care-of-gallatin",
    name: "Family Health Care of Gallatin",
    url: "https://fhcog.net",
    tagline: "Attentive. Experienced. Knowledgeable.",
    category: "Client Site · Healthcare",
    year: "2025",
    image: "/portfolio/site-restore-36.jpg",
    summary:
      "Website, AI front desk and AI-search readiness for a family medicine and primary care practice serving Sumner County and the Nashville metro area.",
    problem:
      "A well-loved, award-winning local practice had a slow single-page site, phone-only answers that stopped at 4:30, and services that search engines and AI tools couldn't find.",
    solution:
      "A faster site with six dedicated service pages, \"Call to book\" as the main button, online new-patient paperwork, and a 24/7 bilingual AI front desk for non-medical questions. Every page serves its own content to search engines and AI tools.",
    highlights: [
      "24/7 English/Spanish AI front desk (non-medical questions only)",
      "Six AI-readable service pages: DOT physicals, walk-ins, diabetes, mental health, weight loss, women's health",
      "Call-first design with a sticky \"Call to book\" bar on mobile",
      "Online new-patient paperwork replaced a print-only packet",
      "Audit score 66 → 85; desktop speed 82 → 97",
    ],
    stack: ["React", "Structured data (JSON-LD)", "llms.txt", "AI chat assistant"],
    outcome:
      "#1 for \"primary care Gallatin TN\", a page about 61% lighter, and patients get answers even after the office closes.",
    metaDescription:
      "Family Health Care of Gallatin — modern site for a trusted family medicine practice in Sumner County. Built by Gadget Guy Media.",
  },
  {
    slug: "glitch",
    name: "Glitch",
    url: "https://glitchai.ezbuildabot.com",
    tagline: "Your personal voice AI.",
    category: "AI App · Voice Assistant",
    year: "2026",
    image: "/portfolio/glitch.jpg",
    summary:
      "Single-user, private voice assistant with an encrypted session. Talk to it like you'd talk to a friend who happens to be a research team.",
    problem:
      "The mainstream AI assistants are optimized for the mainstream. They handle everyone's data, they moderate for the median user, and they can't be shaped around one person's voice, memory, and workflow.",
    solution:
      "Glitch is single-user by design. Encrypted sessions, no multi-tenant middle layer, and a voice-first interface built for the kind of conversation you'd have with a smart assistant who actually knows you.",
    highlights: [
      "Single-user architecture — your session is yours, not shared",
      "Voice-first — talk and listen instead of typing",
      "Encrypted session storage",
      "Email + Google sign-in",
    ],
    stack: ["React", "FastAPI", "Web Speech API", "Claude / GPT", "MongoDB", "Emergent"],
    outcome:
      "A private voice assistant that lives outside the mainstream AI moderation pipeline — for anyone who wants a personal AI that actually feels personal.",
    metaDescription:
      "Glitch — your personal, single-user voice AI assistant with encrypted sessions. Built by Gadget Guy Media.",
  },
  {
    slug: "gatherly",
    name: "Gatherly",
    url: "https://gatherly-zeta.vercel.app",
    tagline: "Don't miss a moment.",
    category: "Web App · Events",
    year: "2026",
    image: "/portfolio/gatherly.jpg",
    summary:
      "Event photo collection made simple: name your event, share a link or QR code, and every guest photo lands in one Google Drive folder.",
    problem:
      "After a wedding, party or reunion, the best photos are scattered across dozens of guests' phones. Group texts compress them, shared albums make people sign up, and half the pictures never get sent at all.",
    solution:
      "Gatherly creates a Google Drive folder for each event automatically. The host shares an upload link or QR code, guests add photos without making an account, and everything goes straight into the host's Drive. A private creator code keeps each event under the host's control.",
    highlights: [
      "Name an event and its Google Drive folder is created automatically",
      "Share with guests by upload link or printable QR code",
      "No guest accounts: guests just open the link and upload",
      "Private creator code keeps each event under the host's control",
    ],
    stack: ["React", "Vercel", "Google Drive API", "QR codes"],
    outcome:
      "Every guest's photos end up in one folder the host already owns, with no app to install and nobody chasing pictures afterward.",
    metaDescription:
      "Gatherly: collect event photos from every guest by link or QR code, straight into one Google Drive folder. No guest accounts. Built by Gadget Guy Media.",
  },
  {
    slug: "gadget-guys-smart-home",
    name: "Gadget Guys Smart Home",
    url: "https://cloud9teck.com",
    tagline: "Expert smart home reviews & guides.",
    category: "Content Site · Consumer",
    year: "2024",
    image: "/portfolio/gadget-guys-blog.jpg",
    summary:
      "An independent, reader-funded smart home review site from Tennessee: 96 in-depth reviews and guides across 11 categories, from 30+ years of hands-on experience.",
    problem:
      "Most smart home content is either affiliate-farm garbage or vendor-parroted spec sheets. Real buyers want an honest 'here's what actually works in a real house.'",
    solution:
      "Every product is bought or borrowed, set up on a real home network and lived with for weeks before it earns a recommendation. Long-form reviews and starter guides cover cameras, locks, lighting, networking and automation, plus a free 5-device starter-kit guide for newsletter subscribers.",
    highlights: [
      "96 reviews and guides across 11 categories",
      "Tested in a lived-in home for weeks, not unboxed for a day",
      "100% independent, never paid to rank",
      "Newsletter with a free smart home starter-kit guide",
    ],
    stack: ["Next.js", "SEO tuning", "Newsletter"],
    outcome:
      "A trusted destination for smart-home buyers who want an actual opinion, not a spec sheet.",
    metaDescription:
      "Gadget Guys Smart Home — honest smart home reviews and setup guides from a 30-year tech veteran. Built by Gadget Guy Media.",
  },
  {
    slug: "gadgetguymedia",
    name: "Gadget Guy Media",
    url: "https://gadgetguymedia.com",
    tagline: "The site you're on right now.",
    category: "Portfolio · This Site",
    year: "2026",
    image: "/portfolio/ggm.jpg",
    summary:
      "The Gadget Guy Media portfolio and services site — designed, built, and shipped in the same weeks-not-months process it sells.",
    problem:
      "A services site has to do two jobs at once: prove the process works, and be an example of the process working.",
    solution:
      "A hand-crafted React + FastAPI site with a live portfolio, case studies, a free Local AI Audit, the AI Front Desk offer, Stripe checkout and billing, a QR-tracked digital business card at /robert and a password-protected analytics dashboard at /robert/stats. All shipped by one person in weeks.",
    highlights: [
      "React frontend + FastAPI backend + MongoDB",
      "Stripe checkout, subscriptions and customer billing portal",
      "SendGrid emails, weekly digest and renewal reminders",
      "QR-tracked digital business card at /robert with /robert/stats analytics",
      "Full SEO + GEO stack: prerendered pages, sitemap, llms.txt, JSON-LD",
    ],
    stack: ["React", "FastAPI", "MongoDB", "Stripe", "SendGrid", "Emergent"],
    outcome:
      "The site is the demo. If it feels fast, credible, and shipped — that's the pitch.",
    metaDescription:
      "The Gadget Guy Media portfolio site — a React + FastAPI proof-of-process shipped in weeks. Built by Robert at Gadget Guy Media.",
  },
];

export const getProjectBySlug = (slug) =>
  projects.find((p) => p.slug === slug);

export default projects;
